import { Theme } from "@mui/material"
import { makeStyles } from "@mui/styles"
import { DataGrid, DataGridProps } from "@mui/x-data-grid"
import { forwardRef, useEffect, useImperativeHandle, useMemo } from "react"
import { useDataGridFilter } from "./hooks/useDataGridFilter"
import { TDataGridSortFilter } from "./hooks/useDataGridFilterState"
import { useDataGridSelectionModel } from "./hooks/useDataGridSelectionModel"

/**
 * Material-ui data-grid using url filter mechanism
 * Filter based on the url can be actived with the isUrlParamsState props
 */
export const GenericDataGrid = forwardRef(
    function GenericDataGrid (
        { id, defaultFilter, isUrlParamsState, ...props }: GenericDataGridProps, 
        ref?: React.ForwardedRef<GenericDataGridRef | undefined>
    ) {
    
    const classes = useStyles()
    const { 
        handleSortModelChange, 
        sortModel, 
        rowPerPage, 
        handlePageSizeChange,
        handlePageChange,
        filter
    } = useDataGridFilter(id || GENERIC_DATA_GRID_ID, isUrlParamsState, defaultFilter)

    const [selectionModel, setSelectionModel] = useDataGridSelectionModel(id || GENERIC_DATA_GRID_ID)

    useEffect(() => {
        if (!isUrlParamsState && !id) {
            console.error(`[DataGrid] This data grid is statefull but it's id is missing. This may create conflict with other grid.`)
        }

        if (props.selectionModel) {
            setSelectionModel(props.selectionModel)
        }
    }, [])

    const sortProps = useMemo((): Partial<DataGridProps> => {
        return {
            sortingMode: "server",
            paginationMode: "server",
            filterMode: "server",
            page: filter?.page,
            pageSize: filter?.pageSize || 20,
            sortModel,
            rowsPerPageOptions: rowPerPage,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
            onSortModelChange: handleSortModelChange,
        }
    }, [sortModel, filter])

    const getRef = (): GenericDataGridRef => ({
        page: filter.page,
        pageSize: filter.pageSize
    })
    
    useImperativeHandle(ref, getRef)

    return (
        <DataGrid
            {...sortProps}
            selectionModel={selectionModel}
            onSelectionModelChange={(newSelectionModel) => setSelectionModel(newSelectionModel)}
            {...props}
            className={`${classes['GenericDataGrid-root']} ${props.className}`}
        />
    )
})

const useStyles = makeStyles((theme: Theme) => ({
    'GenericDataGrid-root': {
        border: 'none !important',
        '& .MuiDataGrid-columnHeaders': {
            backgroundColor: theme.palette.primary.light
        },
        '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: 14
        },
        '& .MuiDataGrid-columnSeparator': {
            display: 'none !important'   
        },
        '& .MuiDataGrid-row': {
            borderBottom: `2px solid ${theme.palette.primary.light}`,
            fontSize: 12,
            color: theme.palette.text.secondary
        },
        '& .MuiDataGrid-cell': {
            color: theme.palette.text.secondary,
        },
        /** Chekbox */
        '& .MuiCheckbox-root svg': {
            color: theme.palette.text.primary,
            fontSize: 18
        },
    }
}))

export const GENERIC_DATA_GRID_ID = 'DataGrid.StateFull.MissingId'

GenericDataGrid.defaultProps = {
    isUrlParamsState: true
}

export type GenericDataGridRef = {
    page?: number
    pageSize?: number
}

export type GenericDataGridProps = Omit<DataGridProps, 'ref'> & {
    /**
     * Unique id for the datagrid
     * It's mandatory if it's 
     */
    id?: string
    /**
     * DataGrid default filter value
     */
    defaultFilter?: TDataGridSortFilter,
    /**
     * Flag telling if the sorting filter is rely on the url.
     * If not activated, the filter will be stored inside a global state. Those values can be retrieved with "useDataGridFilterGlobalState" hooks.
     */
    isUrlParamsState?: boolean
}
